import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import RedoIcon from '@mui/icons-material/Redo';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, Button, ToggleButton, ToggleButtonGroup, Toolbar, Typography } from '@mui/material';
import type { Editor } from '@tiptap/react';
import type React from 'react';

interface RichTextToolbarProps {
  editor: Editor | null;
  sticky?: boolean;
  stickyOffset?: number | string;
}

const RichTextToolbar: React.FC<RichTextToolbarProps> = ({
  editor,
  sticky = false,
  stickyOffset = 0,
}) => {
  if (!editor || editor.isDestroyed) {
    return null;
  }

  return (
    <Toolbar
      variant="dense"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        color: 'text.primary',
        gap: 1,
        flexWrap: 'wrap',
        ...(sticky && {
          position: 'sticky',
          top: stickyOffset,
          zIndex: 10,
        }),
        minHeight: '40px', // Compact
        px: '0 !important', // Remove default padding for tighter integration
      }}
    >
      <ToggleButtonGroup
        size="small"
        sx={{
          '& .MuiToggleButton-root': { color: 'text.primary' },
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <ToggleButton
          value="bold"
          selected={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <FormatBoldIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <FormatItalicIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          value="underline"
          selected={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
        >
          <FormatUnderlinedIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          value="strike"
          selected={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="Strikethrough"
        >
          <StrikethroughSIcon fontSize="small" />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup
        size="small"
        sx={{
          '& .MuiToggleButton-root': { color: 'text.primary' },
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <ToggleButton
          value="bulletList"
          selected={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        >
          <FormatListBulletedIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          value="orderedList"
          selected={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Ordered List"
        >
          <FormatListNumberedIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          value="blockquote"
          selected={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="Blockquote"
        >
          <FormatQuoteIcon fontSize="small" />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup
        size="small"
        sx={{
          '& .MuiToggleButton-root': { color: 'text.primary' },
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <ToggleButton
          value="heading1"
          selected={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          title="Heading 1"
        >
          <Typography fontWeight="bold" variant="body2">
            H1
          </Typography>
        </ToggleButton>
        <ToggleButton
          value="heading2"
          selected={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading 2"
        >
          <Typography fontWeight="bold" variant="body2">
            H2
          </Typography>
        </ToggleButton>
        <ToggleButton
          value="heading3"
          selected={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title="Heading 3"
        >
          <Typography fontWeight="bold" variant="body2">
            H3
          </Typography>
        </ToggleButton>
        <ToggleButton
          value="heading4"
          selected={editor.isActive('heading', { level: 4 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          title="Heading 4"
        >
          <Typography fontWeight="bold" variant="body2">
            H4
          </Typography>
        </ToggleButton>
        <ToggleButton
          value="heading5"
          selected={editor.isActive('heading', { level: 5 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
          title="Heading 5"
        >
          <Typography fontWeight="bold" variant="body2">
            H5
          </Typography>
        </ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UndoIcon />}
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          sx={{ color: 'text.primary', borderColor: 'divider' }}
        >
          Undo
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RedoIcon />}
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          sx={{ color: 'text.primary', borderColor: 'divider' }}
        >
          Redo
        </Button>
      </Box>
    </Toolbar>
  );
};

export default RichTextToolbar;
